import assert from 'assert';
import { type PermitSingleData, type PermitBatchData } from '@uniswap/permit2-sdk';
import { BigintIsh, Ether, Percent, Token } from '@uniswap/sdk-core';
import { Pool, Position, V4PositionManager } from '@uniswap/v4-sdk';
import { Multicall } from '@uniswap/v3-sdk';
import { toJSBI } from '@/utils/bigInt';
import { assetByAddress } from '@/onchain/assets';
import {
  DEFAULT_ADD_LIQUIDITY_SLIPPAGE_TOLERANCE,
  DEFAULT_REMOVE_LIQUIDITY_SLIPPAGE_TOLERANCE,
} from '@/constants';
import {
  type AddLiquidityParams,
  type RemoveLiquidityParams,
  type CollectFeesParams,
  LiquidityAction,
} from '@/types';
import { getPositionManagerForHook } from '@/onchain/utils/hookConfig';

export const getAddLiquidityMulticallArgs = (
  chainId: number,
  recipient: `0x${string}`,
  addLiquidityParams: AddLiquidityParams,
  permitData: PermitSingleData | PermitBatchData,
  permitSignature: `0x${string}`,
) => {
  const { pool, poolState, tickLower, tickUpper, liquidity, liquidityAction, positionId } =
    addLiquidityParams;
  if (liquidityAction === LiquidityAction.MINT) {
    assert(recipient, 'Recipient is required for minting');
  }
  if (liquidityAction === LiquidityAction.INCREASE) {
    assert(positionId, 'Position ID is required for increasing liquidity');
  }
  if (!pool || !poolState || !liquidity) {
    return {
      data: undefined,
      value: 0,
    };
  }

  const asset0 = assetByAddress(chainId, pool.key.currency0);
  const asset1 = assetByAddress(chainId, pool.key.currency1);

  const currency0 = !asset0?.isNative
    ? new Token(chainId, pool.key.currency0, asset0?.exponent ?? 0)
    : Ether.onChain(chainId);

  const currency1 = !asset1?.isNative
    ? new Token(chainId, pool.key.currency1, asset1?.exponent ?? 0)
    : Ether.onChain(chainId);

  const position: Position = new Position({
    pool: new Pool(
      currency0,
      currency1,
      pool.key.fee,
      pool.key.tickSpacing,
      pool.key.hooks,
      toJSBI(poolState.sqrtPriceX96),
      0,
      poolState.tick,
      [],
    ),
    tickLower,
    tickUpper,
    liquidity: toJSBI(liquidity),
  });
  // use the same deadline from the permit
  const deadline = permitData.values.sigDeadline as number;
  const permitDetailsList = Array.isArray(permitData.values.details)
    ? permitData.values.details.map((detail) => {
        return {
          ...detail,
          amount: detail.amount.toString(),
          expiration: detail.expiration as BigintIsh,
          nonce: detail.nonce as BigintIsh,
        };
      })
    : [
        {
          ...permitData.values.details,
          amount: permitData.values.details.amount.toString(),
          expiration: permitData.values.details.expiration as BigintIsh,
          nonce: permitData.values.details.nonce as BigintIsh,
        },
      ];

  const batchPermit = {
    owner: recipient,
    permitBatch: {
      details: permitDetailsList,
      spender: permitData.values.spender,
      sigDeadline: permitData.values.sigDeadline as number,
    },
    signature: permitSignature,
  };

  const slippageTolerance = DEFAULT_ADD_LIQUIDITY_SLIPPAGE_TOLERANCE;

  // check if we should set useNative to the native currency
  const useNative = currency0.isNative ? currency0 : currency1.isNative ? currency1 : undefined;

  let params;
  switch (liquidityAction) {
    case LiquidityAction.MINT:
      params = {
        recipient,
        batchPermit,
        slippageTolerance,
        deadline,
        useNative,
      };
      break;
    case LiquidityAction.INCREASE:
      params = {
        tokenId: positionId,
        batchPermit,
        slippageTolerance,
        deadline,
        useNative,
      };
      break;
    default:
      throw new Error('Invalid liquidity action');
  }

  const { calldata, value } = V4PositionManager.addCallParameters(position, params);

  // Get the position manager address for this hook
  const positionManagerAddress = getPositionManagerForHook(pool.key.hooks, chainId);

  return {
    data: Multicall.decodeMulticall(calldata),
    value,
    positionManagerAddress,
  };
};

export const getRemoveLiquidityArgs = (
  chainId: number,
  removeLiquidityParams: RemoveLiquidityParams,
) => {
  const {
    poolState,
    positionInfo,
    removeLiquidityPercentage,
    burnToken = false,
  } = removeLiquidityParams;

  // The position might have just been exited, so we need to check if it still exists.
  // We use positionInfo.liquidity to verify if the position is still present.
  if (!poolState || !positionInfo || !removeLiquidityPercentage) {
    return {
      args: [],
    };
  }

  const currency0 = new Token(
    chainId,
    positionInfo.poolKey.currency0,
    assetByAddress(chainId, positionInfo.poolKey.currency0)?.exponent ?? 0,
  );
  const currency1 = new Token(
    chainId,
    positionInfo.poolKey.currency1,
    assetByAddress(chainId, positionInfo.poolKey.currency1)?.exponent ?? 0,
  );
  const position: Position = new Position({
    pool: new Pool(
      currency0,
      currency1,
      positionInfo.poolKey.fee,
      positionInfo.poolKey.tickSpacing,
      positionInfo.poolKey.hooks,
      toJSBI(poolState.sqrtPriceX96),
      0,
      poolState.tick,
      [],
    ),
    tickLower: positionInfo.tickLower,
    tickUpper: positionInfo.tickUpper,
    liquidity: toJSBI(positionInfo.liquidity),
  });

  // 2 hours in seconds
  const deadline = Math.floor(Date.now() / 1000) + 2 * 60 * 60;

  const slippageTolerance =
    removeLiquidityParams.slippageTolerance ?? DEFAULT_REMOVE_LIQUIDITY_SLIPPAGE_TOLERANCE;

  // burnToken must be true if the position has no liquidity
  const burnPositionToken = positionInfo.liquidity === 0n ? true : burnToken;

  // Default the liquidity percentage to 100%
  // Calculate the liquidity percentage below if conditions are met
  let liquidityPercentage = new Percent(1);

  // Calculate the liquidity percentage if the position has liquidity and is not being burned
  if (positionInfo.liquidity !== 0n && !burnPositionToken) {
    const liquidityPercentageNumerator = Math.floor(removeLiquidityPercentage * 1e8);
    const liquidityPercentageDenominator = 1e8 * 100;
    liquidityPercentage = new Percent(liquidityPercentageNumerator, liquidityPercentageDenominator);
  }

  const { calldata, value } = V4PositionManager.removeCallParameters(position, {
    tokenId: toJSBI(positionInfo.tokenId),
    liquidityPercentage,
    slippageTolerance,
    deadline,
    burnToken: burnPositionToken,
  });

  // Get the position manager address for this hook
  const positionManagerAddress = getPositionManagerForHook(positionInfo.poolKey.hooks, chainId);

  return { data: [calldata], value, positionManagerAddress };
};

export const getCollectFeesArgs = (chainId: number, collectFeesParams: CollectFeesParams) => {
  const { poolState, positionInfo, recipient } = collectFeesParams;

  if (!poolState || !positionInfo || !recipient) {
    return {
      args: [],
    };
  }

  const currency0 = new Token(
    chainId,
    positionInfo.poolKey.currency0,
    assetByAddress(chainId, positionInfo.poolKey.currency0)?.exponent ?? 0,
  );
  const currency1 = new Token(
    chainId,
    positionInfo.poolKey.currency1,
    assetByAddress(chainId, positionInfo.poolKey.currency1)?.exponent ?? 0,
  );
  const position: Position = new Position({
    pool: new Pool(
      currency0,
      currency1,
      positionInfo.poolKey.fee,
      positionInfo.poolKey.tickSpacing,
      positionInfo.poolKey.hooks,
      toJSBI(poolState.sqrtPriceX96),
      0,
      poolState.tick,
      [],
    ),
    tickLower: positionInfo.tickLower,
    tickUpper: positionInfo.tickUpper,
    liquidity: toJSBI(positionInfo.liquidity),
  });

  // 2 hours in seconds
  const deadline = Math.floor(Date.now() / 1000) + 2 * 60 * 60;

  const { calldata, value } = V4PositionManager.collectCallParameters(position, {
    tokenId: toJSBI(positionInfo.tokenId),
    // TODO: check if 100% makes sense
    slippageTolerance: new Percent(1),
    deadline,
    recipient,
  });

  // Get the position manager address for this hook
  const positionManagerAddress = getPositionManagerForHook(positionInfo.poolKey.hooks, chainId);

  return { data: [calldata], value, positionManagerAddress };
};
